import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, LogOut, Plus, Search, User } from 'lucide-react';
import {
  Avatar,
  Badge,
  Button,
  CountBadge,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Modal,
  SearchInput,
  cn,
} from '@micarahr/web-ui';
import { roleLabel } from '@micarahr/permissions/web';
import { QUICK_CREATE } from '../../app/navigation';
import { usePermission, useUiStore, useAuthStore } from '../../stores';
import { authService, notificationService, searchService } from '../../services';
import { LIVE_QUERY, queryKeys } from '../../lib/query';

/** How often an open console re-reads the unread count. */
const UNREAD_POLL_MS = 60_000;
import { dataSourceLabel, env } from '../../config/env';
import { formatRelative } from '../../lib/format';
import { notificationCategoryLabel } from '../../lib/notification-text';

/**
 * The top command bar.
 *
 * Light, not dark (§8). Search is real: it queries the services, is debounced,
 * is keyboard-navigable, and only searches categories the signed-in user may
 * open — a result they cannot reach would leak that the record exists.
 */

export function CommandBar() {
  const navigate = useNavigate();
  const { session, canAny } = usePermission();
  const setSession = useAuthStore((state) => state.setSession);
  const queryClient = useQueryClient();
  const paletteOpen = useUiStore((state) => state.commandPaletteOpen);
  const setPaletteOpen = useUiStore((state) => state.setCommandPaletteOpen);

  /*
   * The bell keeps up with an open tab. It used to be read once, on the full
   * page load: a notification that arrived while the console sat open showed
   * only after a reload. Re-read every minute while the tab is visible, and
   * whenever it regains focus; a realtime event refreshes it sooner.
   */
  const unread = useQuery({
    queryKey: queryKeys.notifications.unread(),
    queryFn: () => notificationService.unreadCount(),
    ...LIVE_QUERY,
    refetchInterval: UNREAD_POLL_MS,
  });

  // Cmd/Ctrl-K is the shortcut every operator of a tool like this expects.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setPaletteOpen]);

  const quickCreate = QUICK_CREATE.filter((action) => canAny(action.permissions));

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        className={cn(
          'flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border border-line-strong bg-surface-muted px-3 text-left',
          'text-ink-muted transition-colors hover:bg-neutral-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30',
          'lap:max-w-md',
        )}
      >
        <Search className="h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-base">
          Search employees, requests, payroll…
        </span>
        <kbd className="hidden shrink-0 rounded border border-line-strong bg-surface px-1.5 py-0.5 font-mono text-micro text-ink-muted lap:inline">
          ⌘K
        </kbd>
      </button>

      {/* `ml-auto` is what pushes this group to the right edge. The search box
          takes the remaining space but caps at `max-w-md`, so without it the
          actions sit immediately after the search and the whole bar bunches on
          the left. */}
      <div className="ml-auto flex shrink-0 items-center gap-2">
        {quickCreate.length > 0 ? (
          <Menu>
            <MenuTrigger asChild>
              <Button variant="primary" size="sm" leadingIcon={<Plus className="h-4 w-4" />}>
                <span className="hidden tl:inline">Create</span>
              </Button>
            </MenuTrigger>
            <MenuContent>
              <MenuLabel>Quick create</MenuLabel>
              {quickCreate.map((action) => {
                const Icon = action.icon;
                return (
                  <MenuItem
                    key={action.to}
                    onSelect={() => {
                      /*
                       * Path and query apart. Handed whole to `to`, the query
                       * was matched as part of the path: from the page the
                       * action targets, "Add candidate" resolved to the parent
                       * route and the form never opened.
                       */
                      const [path, query] = action.to.split('?');
                      void navigate({
                        to: path,
                        search: Object.fromEntries(new URLSearchParams(query ?? '')) as never,
                      });
                    }}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
                    {action.label}
                  </MenuItem>
                );
              })}
            </MenuContent>
          </Menu>
        ) : null}

        <NotificationMenu unreadCount={unread.data ?? 0} />

        {/*
         * Which data source is live, not a fixed label.
         *
         * A badge reading "Prototype" while the app talks to the real API is
         * worse than no badge: it tells somebody the figures on screen are
         * fictional when they are not, and it tells them nothing when they
         * are. The colour follows the same rule — warning for mock, neutral
         * for the API, because mock data in front of somebody who thinks it
         * is real is the state worth flagging.
         */}
        {env().isProduction && env().dataSource === 'api' ? null : (
          <Badge
            tone={env().dataSource === 'api' ? 'neutral' : 'warning'}
            size="sm"
            className="hidden lap:inline-flex"
          >
            {dataSourceLabel(env())}
          </Badge>
        )}

        {session ? (
          <Menu>
            <MenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 rounded-md p-1 hover:bg-neutral-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
                aria-label="Account menu"
              >
                <Avatar name={session.displayName} size="sm" />
                <div className="hidden min-w-0 text-left lap:block">
                  <p className="truncate text-sm font-medium text-ink">{session.displayName}</p>
                  <p className="truncate text-micro text-ink-muted">
                    {roleLabel(session.primaryRole)}
                  </p>
                </div>
              </button>
            </MenuTrigger>
            <MenuContent className="min-w-[280px]">
              <MenuLabel>Signed in as</MenuLabel>
              <div className="px-2 pb-2">
                <p className="text-base font-medium text-ink">{session.displayName}</p>
                <p className="truncate text-caption text-ink-muted">{session.email}</p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {session.roles.map((role) => (
                    <Badge key={role} tone="brand" size="sm">
                      {roleLabel(role)}
                    </Badge>
                  ))}
                </div>
              </div>
              {/*
               * Development builds only. In production this menu was a
               * one-click sign-in to any seeded administrator account with a
               * password hardcoded in the bundle. Static `import.meta.env.PROD`
               * so the bundler strips it rather than merely not rendering it.
               */}
              {import.meta.env.PROD ? null : (
                <>
                  <MenuSeparator />
                  <MenuLabel>Switch demonstration account</MenuLabel>
                  {authService.demoAccounts().map((account) => (
                    <MenuItem
                      key={account.email}
                      onSelect={async () => {
                        const next = await authService.switchTo(account.email);
                        // Another account's cache is another person's data.
                        queryClient.clear();
                        setSession(next);
                        navigate({ to: '/' });
                      }}
                    >
                      <User className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{account.name}</span>
                      {account.email === session.email ? (
                        <Check className="h-3.5 w-3.5 shrink-0 text-brand" aria-hidden />
                      ) : null}
                    </MenuItem>
                  ))}
                </>
              )}
              <MenuSeparator />
              <MenuItem
                onSelect={async () => {
                  await authService.signOut(session);
                  setSession(null);
                  /*
                   * The cache belongs to the account that filled it. Kept, the
                   * next sign-in in this tab was served the previous one's
                   * answers for as long as they stayed fresh — the unread
                   * badge among them, still counting notifications already
                   * marked read.
                   */
                  queryClient.clear();
                }}
              >
                <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                Sign out
              </MenuItem>
            </MenuContent>
          </Menu>
        ) : null}
      </div>

      <SearchPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* Notifications                                                               */
/* -------------------------------------------------------------------------- */

function NotificationMenu({ unreadCount }: { unreadCount: number }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /*
   * The badge and the list are separate queries — the count is not derived
   * from a paginated list — so a read has to refresh both. Refetching only
   * the list left the badge at its old number after "Mark all read", although
   * the server had already cleared every one.
   */
  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all() });
  const notifications = useQuery({
    queryKey: queryKeys.notifications.list({ limit: 8 }),
    queryFn: () => notificationService.list({}),
  });
  // A new count means new rows: the list under the bell follows the badge.
  const seenCount = useRef(unreadCount);
  useEffect(() => {
    if (seenCount.current === unreadCount) return;
    seenCount.current = unreadCount;
    void queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
  }, [queryClient, unreadCount]);

  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          type="button"
          className="relative rounded-md p-2 text-ink-secondary hover:bg-neutral-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        >
          <Bell className="h-4 w-4" aria-hidden />
          {unreadCount > 0 ? (
            <span className="absolute -right-0.5 -top-0.5">
              <CountBadge count={unreadCount} tone="danger" />
            </span>
          ) : null}
        </button>
      </MenuTrigger>
      <MenuContent className="max-h-[420px] w-[360px] overflow-y-auto">
        <div className="flex items-center justify-between px-2 py-1.5">
          <span className="text-micro font-semibold uppercase tracking-wide text-ink-subtle">
            Notifications
          </span>
          <button
            type="button"
            className="text-caption text-brand hover:underline"
            onClick={async () => {
              await notificationService.markAllRead();
              await refresh();
            }}
          >
            Mark all read
          </button>
        </div>
        <MenuSeparator />
        {(notifications.data ?? []).slice(0, 8).map((notification) => (
          <MenuItem
            key={notification.id}
            onSelect={async () => {
              await notificationService.markRead(notification.id);
              void refresh();
              navigate({ to: notification.href }).catch(() => undefined);
            }}
            className="items-start"
          >
            <span
              className={cn(
                'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
                notification.read ? 'bg-transparent' : 'bg-brand',
              )}
              aria-hidden
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-base font-medium text-ink">
                {notification.title}
              </span>
              <span className="mt-0.5 block text-caption leading-snug text-ink-muted">
                {notification.body}
              </span>
              <span className="mt-1 flex items-center gap-2">
                <Badge size="sm" tone="neutral">
                  {notificationCategoryLabel(notification.category)}
                </Badge>
                <span className="text-micro text-ink-subtle">
                  {formatRelative(notification.createdAt)}
                </span>
              </span>
            </span>
          </MenuItem>
        ))}
        {(notifications.data ?? []).length === 0 ? (
          <p className="px-3 py-6 text-center text-base text-ink-muted">Nothing yet.</p>
        ) : null}
      </MenuContent>
    </Menu>
  );
}

/* -------------------------------------------------------------------------- */
/* Search palette                                                              */
/* -------------------------------------------------------------------------- */

function SearchPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const { session } = usePermission();

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term), 180);
    return () => clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    if (!open) {
      setTerm('');
      setDebounced('');
      setActiveIndex(0);
    }
  }, [open]);

  const results = useQuery({
    queryKey: queryKeys.search(debounced),
    queryFn: () => searchService.query(debounced, session?.permissions ?? []),
    enabled: debounced.trim().length >= 2,
  });

  const rows = useMemo(() => results.data ?? [], [results.data]);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof rows>();
    for (const row of rows) {
      map.set(row.categoryLabel, [...(map.get(row.categoryLabel) ?? []), row]);
    }
    return [...map.entries()];
  }, [rows]);

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Search" width="lg">
      <div className="pb-4">
        <SearchInput
          autoFocus
          value={term}
          placeholder="Employees, requests, payroll periods, candidates, assets…"
          onChange={(event) => {
            setTerm(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActiveIndex((index) => Math.min(index + 1, rows.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter' && rows[activeIndex]) {
              event.preventDefault();
              onOpenChange(false);
              navigate({ to: rows[activeIndex]!.href }).catch(() => undefined);
            }
          }}
        />

        <div className="mt-3 max-h-[420px] overflow-y-auto">
          {debounced.trim().length < 2 ? (
            <p className="py-8 text-center text-base text-ink-muted">
              Type at least two characters. Results are limited to what your roles allow you to
              open.
            </p>
          ) : results.isPending ? (
            <p className="py-8 text-center text-base text-ink-muted">Searching…</p>
          ) : rows.length === 0 ? (
            <p className="py-8 text-center text-base text-ink-muted">
              Nothing matched “{debounced}”.
            </p>
          ) : (
            grouped.map(([label, entries]) => (
              <div key={label} className="mb-3 last:mb-0">
                <h3 className="mb-1 px-1 text-micro font-semibold uppercase tracking-wide text-ink-subtle">
                  {label}
                </h3>
                <ul>
                  {entries.map((entry) => {
                    const index = rows.indexOf(entry);
                    return (
                      <li key={entry.id}>
                        <Link
                          to={entry.href}
                          onClick={() => onOpenChange(false)}
                          onMouseEnter={() => setActiveIndex(index)}
                          className={cn(
                            'block rounded-md px-2 py-1.5',
                            index === activeIndex ? 'bg-brand-soft' : 'hover:bg-neutral-soft',
                          )}
                        >
                          <span className="block truncate text-base font-medium text-ink">
                            {entry.title}
                          </span>
                          <span className="block truncate text-caption text-ink-muted">
                            {entry.subtitle}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
